import React, { ChangeEvent, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  MdDashboard, 
  MdAccountBalance, 
  MdReceipt, 
  MdInsertChart,
  MdPerson,
  MdSettings,
  MdLogout,
  MdBusiness,
  MdKeyboardArrowDown,
  MdTrendingUp
} from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { organization, organizations, switchOrganization } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  const handleOrgChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const orgId = parseInt(e.target.value);
    if (orgId) {
      switchOrganization(orgId);
    }
  };

  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <Nav>
      <NavContainer>
        <LeftSection>
          <Brand to="/dashboard">
            <BrandIcon>R</BrandIcon>
            <BrandText>Racofin</BrandText>
          </Brand>

          <NavMenu>
            <NavLinkItem to="/dashboard" $active={isActive('/dashboard')}>
              <MdDashboard />
              <span>Dashboard</span>
            </NavLinkItem>
            <NavLinkItem to="/finance-health" $active={isActive('/finance-health')}>
              <MdTrendingUp />
              <span>Health</span>
            </NavLinkItem>
            <NavLinkItem to="/accounting" $active={isActive('/accounting')}>
              <MdAccountBalance />
              <span>Accounting</span>
            </NavLinkItem>
            <NavLinkItem to="/accounting/journal-entry" $active={isActive('/accounting/journal-entry')}>
              <MdReceipt />
              <span>Journal</span>
            </NavLinkItem>
            <NavLinkItem to="/accounting/reports" $active={isActive('/accounting/reports')}>
              <MdInsertChart />
              <span>Reports</span>
            </NavLinkItem>
          </NavMenu>
        </LeftSection>

        <RightSection>
          {organizations.length > 1 && (
            <OrgSelectorContainer>
              <OrgIcon><MdBusiness /></OrgIcon>
              <OrgSelector
                value={organization?.id || ''}
                onChange={handleOrgChange}
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.org_name}
                  </option>
                ))}
              </OrgSelector>
            </OrgSelectorContainer>
          )}

          <UserMenuContainer onClick={() => setShowUserMenu(!showUserMenu)}>
            <UserButton>
              <UserAvatar>
                {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
              </UserAvatar>
              <UserInfo>
                <UserName>{user?.full_name}</UserName>
                <OrgName>{organization?.org_name}</OrgName>
              </UserInfo>
              <ChevronIcon $open={showUserMenu}>
                <MdKeyboardArrowDown />
              </ChevronIcon>
            </UserButton>
            {showUserMenu && (
              <DropdownMenu>
                <DropdownLink to="/profile" onClick={() => setShowUserMenu(false)}>
                  <MdPerson />
                  <span>Profile</span>
                </DropdownLink>
                <DropdownLink to="/settings" onClick={() => setShowUserMenu(false)}>
                  <MdSettings />
                  <span>Settings</span>
                </DropdownLink>
                <Divider />
                <DropdownButton onClick={handleLogout}>
                  <MdLogout />
                  <span>Logout</span>
                </DropdownButton>
              </DropdownMenu>
            )}
          </UserMenuContainer>
        </RightSection>
      </NavContainer>
    </Nav>
  );
};

const Nav = styled.nav`
  background: white;
  border-bottom: 1px solid #E5E7EB;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const NavContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  flex: 1;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const BrandIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
`;

const BrandText = styled.span`
  font-size: 20px;
  font-weight: 700;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavMenu = styled.div`
  display: flex;
  gap: 4px;

  @media (max-width: 968px) {
    display: none;
  }
`;

const NavLinkItem = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  color: ${props => props.$active ? '#1e3a8a' : '#6B7280'};
  background: ${props => props.$active ? '#eff6ff' : 'transparent'};
  text-decoration: none;
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '500'};
  border-radius: 8px;
  transition: all 0.2s;

  svg {
    font-size: 20px;
  }

  &:hover {
    background: ${props => props.$active ? '#eff6ff' : '#F3F4F6'};
    color: #1e3a8a;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const OrgSelectorContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #F9FAFB;
  border-radius: 8px;
  border: 1px solid #E5E7EB;

  @media (max-width: 768px) {
    display: none;
  }
`;

const OrgIcon = styled.div`
  color: #6B7280;
  display: flex;
  align-items: center;
  font-size: 18px;
`;

const OrgSelector = styled.select`
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  outline: none;
  padding-right: 20px;

  &:focus {
    outline: none;
  }
`;

const UserMenuContainer = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px 6px 6px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #1e3a8a;
    background: #F9FAFB;
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: 14px;
  color: #111827;
  font-weight: 600;
  line-height: 1.2;
`;

const OrgName = styled.span`
  font-size: 12px;
  color: #6B7280;
  line-height: 1.2;
`;

const ChevronIcon = styled.div<{ $open: boolean }>`
  color: #6B7280;
  font-size: 20px;
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0deg)'};

  @media (max-width: 768px) {
    display: none;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid #E5E7EB;
  min-width: 200px;
  padding: 8px;
  z-index: 1000;
`;

const DropdownLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  background: none;
  border: none;
  border-radius: 6px;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s;

  svg {
    font-size: 18px;
    color: #6B7280;
  }

  &:hover {
    background: #F3F4F6;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  background: none;
  border: none;
  border-radius: 6px;
  color: #DC2626;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: #FEE2E2;
  }
`;

const Divider = styled.hr`
  margin: 8px 0;
  border: none;
  border-top: 1px solid #E5E7EB;
`;

export default Navbar;
